import type { FocusEvent, Ref } from 'react';
import type { InputAttributes, NumberFormatValues } from 'react-number-format';

import { Input } from '@connected/ui/components/input';
import { isNil } from 'lodash-es';
import { NumericFormat } from 'react-number-format';

import type { HandleChange } from './types';

import { delimiter, thousandSeparator } from '../utils/formatter';

export type NumberFieldProps = {
    name: string;
    label?: string;
    ariaLabel?: string;
    inputMode?: 'numeric' | 'decimal';
    inputRef?: Ref<any>;
    value?: number | null;
    placeholder?: string;
    required?: boolean;
    errorMessage?: string;
    disabled?: boolean;
    onChange: HandleChange<number | null>;
    onBlur?: (e: FocusEvent<any>) => void;
    onFocus?: (e: FocusEvent<any>) => void;
    onPlusMinus?: () => void;
    onClear?: () => void;
    showClearBtn?: boolean;
    clearLabel?: string;
    min?: number;
    max?: number;
    step?: number;
    clamp?: boolean;
    maxPrecision?: number;
    showThousandSeparator?: boolean;
    leftChild?: string;
    className?: string;
    customInput?: React.ComponentType<InputAttributes>;
};

export function NumberField({
    name,
    label,
    ariaLabel,
    inputMode,
    inputRef,
    value,
    placeholder,
    disabled = false,
    onChange,
    onPlusMinus,
    clearLabel,
    min,
    max,
    step = 1,
    clamp = false,
    maxPrecision,
    showThousandSeparator,
    leftChild,
    className,
    customInput = Input,
    ...props
}: NumberFieldProps) {
    const isAllowed = ({ floatValue }: NumberFormatValues): boolean => {
        if (!floatValue) {
            return true;
        }

        // These checks will prevent users from typing more than the max or less than the min
        // Before, it would visually let you type bigger/smaller numbers while the effective value stayed capped
        if (max && floatValue > max) {
            onChange(max);
            return false;
        }

        if (min && floatValue < min) {
            onChange(min);
            return false;
        }

        return true;
    };

    return (
        <NumericFormat
            id={name}
            name={name}
            customInput={customInput}
            type="text"
            decimalScale={maxPrecision}
            fixedDecimalScale={!isNil(maxPrecision)}
            allowedDecimalSeparators={['.', ',']}
            inputMode={inputMode}
            disabled={disabled}
            decimalSeparator={delimiter}
            thousandSeparator={showThousandSeparator ? thousandSeparator : undefined}
            min={min}
            max={max}
            step={step}
            valueIsNumericString
            value={value}
            placeholder={placeholder}
            onValueChange={(e) => {
                if (e !== undefined && e.floatValue !== undefined) {
                    onChange(e.floatValue);
                }
                if (e.floatValue === undefined) {
                    onChange(null);
                }
            }}
            isAllowed={clamp ? isAllowed : undefined}
            autoComplete="off"
            getInputRef={inputRef}
            onKeyDown={e => e.key === 'Enter' && e.preventDefault()}

            {...props}
        />
    );
};
